
import type { RequestStatus } from '@empathyco/x-components'
import type { ComputedRef, PropType, Ref, SetupContext } from 'vue'
import type { RegularFilter } from '../../models/store/filters/filter'
import type { EpbSelectOption } from '../shared/epb-select'
import type { FieldValueSelectValue } from '../shared/field-value-select.types'
import { computed, defineComponent, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue'
import { useEndpoints } from '../../composables/use-endpoints'
import { EndpointContextEnum } from '../../enums'
import { ColorEnum } from '../../enums/color.enum'
import FiltersAside from '../filters/filters-aside.component.vue'
import { UiProgressLinear } from '../keen-ui'
import { EpbBadge } from '../shared/epb-badge'
import { EpbText } from '../shared/epb-text'
import FieldValueSelect from '../shared/field-value-select.component.vue'
import XExtraParams from '../x/x-extra-params.component.vue'
import XResultsGrid from '../x/x-results-grid.component.vue'
import XSearchbox from '../x/x-searchbox.component.vue'
import ToolingPreviewNoResults from './tooling-preview-no-results.component.vue'

export interface SetupValues {
  baseClass: string
  EndpointContextEnum: typeof EndpointContextEnum
  browseFieldValue: Ref<FieldValueSelectValue>
  filtersAreValid: ComputedRef<boolean>
  hasResults: ComputedRef<boolean>
  emptyQuery: ComputedRef<boolean>
  loading: ComputedRef<boolean>
  totalResults: ComputedRef<number>
  BRAND_5: string
  filtersToDisplay: ComputedRef<Record<string, RegularFilter>>
  computedExtraParams: ComputedRef<Record<string, any>>
  setFieldValue: (event: FieldValueSelectValue) => void
}

export default defineComponent({
  name: 'ToolingPreview',
  components: {
    XSearchbox,
    FieldValueSelect,
    FiltersAside,
    UiProgressLinear,
    XResultsGrid,
    XExtraParams,
    ToolingPreviewNoResults,
    EpbBadge,
    EpbText,
  },
  props: {
    endpointId: {
      type: String,
      default: 'search',
    },
    context: {
      type: String as PropType<EndpointContextEnum>,
      default: 'query',
    },
    filters: {
      type: Object as PropType<Record<string, RegularFilter>>,
      required: true,
    },
    extraParams: {
      type: Object as PropType<Record<string, any>>,
      default: undefined,
    },
    messages: {
      type: Object,
      required: true,
    },
    showSearchbox: {
      type: Boolean,
      default: true,
    },
    browseFieldOptions: {
      type: Array as PropType<Array<EpbSelectOption>>,
      default: () => [],
    },
    debug: Boolean,
  },
  setup(props: Record<string, any>, ctx: SetupContext): SetupValues {
    const { emit } = ctx
    const {
      proxy: { $x },
    } = getCurrentInstance()

    const browseFieldValue = ref({ name: '', value: '' })

    const { endpoints } = useEndpoints()

    watch(
      () => [props.endpointId, props.context, endpoints.value],
      () => {
        const defaultEndpoint = endpoints.value.find(
          endpoint => endpoint.value === props.endpointId,
        )
        if (
          props.context === EndpointContextEnum.FILTER &&
          !browseFieldValue.value.name &&
          !browseFieldValue.value.value
        ) {
          browseFieldValue.value = {
            name: defaultEndpoint.defaultBrowseField,
            value: defaultEndpoint.defaultBrowseValue,
          }
        }
      },
      { immediate: true },
    )

    const loading = computed((): boolean => $x.status.search === 'loading')

    const totalResults = computed((): number => {
      return $x.totalResults
    })

    const filtersAreValid = computed((): boolean => {
      return Object.values(props.filters).every(
        (filter: RegularFilter) => !filter.config.mandatory || !!filter.selectedValues.length,
      )
    })

    const hasResults = computed((): boolean => $x.results.length > 0)

    const emptyQuery = computed((): boolean => !$x.query.search)

    const filtersToDisplay = computed((): Record<string, RegularFilter> => props.filters)

    const setFieldValue = event => {
      browseFieldValue.value = {
        name: event.name,
        value: event.value,
      }

      if (event.name && event.value) {
        $x.emit('UserAcceptedAQuery', `${event.name}:${event.value}`)
      }
    }

    const computedExtraParams = computed((): Record<string, any> => {
      if (props.browseFieldOptions.length > 0) {
        return {
          ...props.extraParams,
          browseField: browseFieldValue.value.name,
          browseValue: browseFieldValue.value.value,
        }
      }
      return props.extraParams || {}
    })

    watch(
      () => $x.status.search,
      (status: RequestStatus): void => {
        if (status === 'error') {
          emit('error')
        }
      },
    )

    onBeforeUnmount(() => {
      $x.emit('UserPressedClearSearchBoxButton')
    })

    return {
      baseClass: 'epb-tooling-preview',
      EndpointContextEnum,
      browseFieldValue,
      emptyQuery,
      loading,
      totalResults,
      filtersAreValid,
      hasResults,
      BRAND_5: ColorEnum.BRAND_5,
      filtersToDisplay,
      computedExtraParams,
      setFieldValue,
    }
  },
})
